<script setup lang="ts">
import { PaginationRequestModel, PaginationResponseModel } from '@/models';
import {
  TirPmIconView,
  TirPmNotificationEventBus,
  TirPmNotificationModel,
  TirPmPagination,
  TirPmScrollbar,
} from 'tir-components';
import { computed, onMounted, PropType, ref, watch } from 'vue';
import { MonitoringsAdapterService } from '../../adapter-service';
import {
  GetProjectTasksRequestModel,
  TaskColumnModel,
  TaskFilterModel,
  TaskModel,
} from '../../models';
import { useRouter } from 'vue-router';
import { TaskViewElementEnum } from '../../enums';
import { debounce } from 'tir-pm-debounce';
import { TaskDrawer, TaskDrawerSettingsModel } from '@/components';
import GetEmployeesWithAccessToProjectItemModel from '../../models/GetEmployeesWithAccessToProjectItemModel';
import { MagnifyingGlassIcon } from 'tir-style-system/icons/outline';
import { MonitoringsSkeleton } from '../../monitorings-skeleton';
import { TaskCardMonitoring } from '../../components/task-card-monitoring';
import { TaskCardMonitoringHeader } from '../../components/task-card-monitoring-header';

/**адаптер */
const adapter = new MonitoringsAdapterService();

/**Роутер */
const router = useRouter();

const props = defineProps({
  /**Поиск */
  search: { type: String, required: true },
  /**Выбранный исполнитель */
  selectPerformer: { type: Object as PropType<GetEmployeesWithAccessToProjectItemModel> },
  /**Поиск исполнителей */
  searchPerformers: { type: String },
  /**Начальная дата */
  selectDateStart: { type: Date },
  /**Конечная дата */
  selectDateEnd: { type: Date },
});

const emit = defineEmits<{
  (e: 'openTaskDrawer', taskId: number);
  /**При получении общего затрачеваемого времени */
  (e: 'getTotalWorkTime', time: number);
}>();

/**id проекта */
const projectId = router.currentRoute.value.params.projectId;
/**Загрузка */
const loading = ref(false);

/**Дефолтное кол-во элементов на странице */
const DEFAULT_PAGE_STEP = 10;

/**Колонки */
const columns: TaskColumnModel[] = [
  new TaskColumnModel({
    Type: TaskViewElementEnum.Performers,
    Name: 'Исполнители',
    Width: '4.125rem',
    MinWidth: '4.1875rem',
    Align: 'left',
  }),
  new TaskColumnModel({
    Type: TaskViewElementEnum.Namespace,
    Name: '',
    Width: '1.5rem',
    MinWidth: '1.5rem',
  }),
  new TaskColumnModel({
    Type: TaskViewElementEnum.Queue,
    Name: 'Очередь',
    Width: '9.375rem',
    MinWidth: '9.375rem',
  }),
  new TaskColumnModel({
    Type: TaskViewElementEnum.Number,
    Name: '№',
    Width: '5rem',
    MinWidth: '5rem',
  }),
  new TaskColumnModel({
    Type: TaskViewElementEnum.Name,
    Name: 'Название',
    Width: '50vw',
    MinWidth: '20rem',
  }),
  new TaskColumnModel({
    Type: TaskViewElementEnum.Status,
    Name: 'Статус',
    Width: '7.688rem',
    MinWidth: '7.688rem',
    Align: 'right',
  }),
  new TaskColumnModel({
    Type: TaskViewElementEnum.Priority,
    Name: 'Приоритет',
    Width: '4rem',
    MinWidth: '4rem',
    Align: 'center',
  }),
  new TaskColumnModel({
    Type: TaskViewElementEnum.Difficulty,
    Name: 'Сложность',
    Width: '2rem',
    MinWidth: '2rem',
    Align: 'center',
  }),
  new TaskColumnModel({
    Type: TaskViewElementEnum.EstimateTime,
    Name: 't (оценка)',
    Width: '5rem',
    MinWidth: '5rem',
    Align: 'center',
  }),
  new TaskColumnModel({
    Type: TaskViewElementEnum.WorkTime,
    Name: 't (затрачено)',
    Width: '5rem',
    MinWidth: '5rem',
    Align: 'center',
  }),
];

/**Кол-во элементов на странице */
const pageStep = ref(DEFAULT_PAGE_STEP);

/**Ссылка на шторку задачи */
const $taskDrawer = ref<InstanceType<typeof TaskDrawer>>();

/**Список задач */
const tasks = ref([]);

/**Общее затраченное время */
const totalWorkTime = ref<number>(0);

/**Страница */
const pageResponse = ref<PaginationResponseModel<Array<TaskModel>>>();

/**Настройки видимости */
const viewSettings = ref([
  TaskViewElementEnum.Performers,
  TaskViewElementEnum.Namespace,
  TaskViewElementEnum.Queue,
  TaskViewElementEnum.Name,
  TaskViewElementEnum.Number,
  TaskViewElementEnum.Status,
  TaskViewElementEnum.Priority,
  TaskViewElementEnum.EstimateTime,
  TaskViewElementEnum.WorkTime,
]);

/**Текущая страница */
const currentPageRequest = computed(() => {
  if (pageResponse.value)
    return new PaginationRequestModel({
      Skip: pageResponse.value.Size * (pageResponse.value.Current - 1),
      Take: pageStep.value,
    });
});

/**Кол-во страниц */
const pagesCount = computed(
  () => pageResponse.value && Math.ceil(pageResponse.value.Count / pageResponse.value.Size),
);

/** Id выбранного исполнителя */
const selectedPerformerId = computed(() => {
  return props.selectPerformer && props.selectPerformer.UserId
    ? [props.selectPerformer.UserId]
    : [];
});

/** Получить список задач */
const getTasks = async (page: PaginationRequestModel, clear?: Boolean) => {
  loading.value = true;
  if (clear) {
    tasks.value = [];
  }
  const response = await adapter.getTasksByProjectId(
    new GetProjectTasksRequestModel({
      ProjectId: +projectId,
      Filter: new TaskFilterModel({
        Search: props.search || '',
        Performers: selectedPerformerId.value,
      }),
      DateStart: props.selectDateStart,
      DateEnd: props.selectDateEnd,
      Page: page,
    }),
  );

  if (response.IsSuccess) {
    pageResponse.value = response;
    totalWorkTime.value = response.TotalWorkTime;
    tasks.value = response?.Items.sort((a, b) => b.Id - a.Id) || [];
  } else {
    TirPmNotificationEventBus.emit(
      'add',
      new TirPmNotificationModel({
        title: response.ErrorMessage ?? 'Ошибка получения списка задач',
        type: 'error',
        group: 'general',
        life: 5000,
        closable: true,
      }),
    );
  }

  loading.value = false;
  return response.IsSuccess;
};

/**Открыть шторку с задачей */
const openTaskDrawer = (taskId: number) => {
  $taskDrawer.value?.open(
    new TaskDrawerSettingsModel({
      Mode: 'edit',
      TaskId: taskId,
    }),
  );
};

/**Обновить список */
const updateList = async () => {
  await getTasks(
    new PaginationRequestModel({
      Skip: 0,
      Take: pageStep.value,
    }),
  );
};

/**Изменить кол-во элементов на странице */
const changePageStep = async (value: number) => {
  pageStep.value = value;
  await getTasks(
    new PaginationRequestModel({
      Skip: 0,
      Take: pageStep.value,
    }),
  );
};

/**Обновить текущую страницу */
const changeCurrentPage = async (value: number) => {
  if (!pageResponse.value) return;

  await getTasks(
    new PaginationRequestModel({
      Skip: pageResponse.value.Size * (value - 1),
      Take: pageStep.value,
    }),
  );
};

/**При поиске через debounce */
const debounceOnSearch = debounce(async () => {
  await getTasks(currentPageRequest.value, true);
  emit('getTotalWorkTime', totalWorkTime.value);
}, 300);

watch(
  () => props.search,
  () => {
    debounceOnSearch();
  },
);
watch(
  () => props.selectPerformer,
  async () => {
    await updateList();
    emit('getTotalWorkTime', totalWorkTime.value);
  },
);

watch(
  () => [props.selectDateStart, props.selectDateEnd],
  async () => {
    await updateList();
    emit('getTotalWorkTime', pageResponse.value.TotalWorkTime);
  },
  { immediate: true },
);

onMounted(async () => {
  await getTasks(
    new PaginationRequestModel({
      Skip: 0,
      Take: pageStep.value,
    }),
  );
});
</script>

<template>
  <div v-if="!loading" class="monitoring-tickets-table__block">
    <TirPmScrollbar
      v-if="tasks.length"
      class="monitoring-tickets-table__scrollbar"
      :show-on-hover="true"
    >
      <div class="monitoring-tickets-table__main">
        <div class="monitoring-tickets-table__list">
          <TaskCardMonitoringHeader
            class="monitoring-tickets-table__columns-header"
            :columns="columns"
            :viewSettings="viewSettings"
          />
          <TaskCardMonitoring
            v-for="task in tasks"
            monitoringTicketPage
            isPerformersLeftBlock
            :key="task.Id"
            :task="task"
            :columns="columns"
            :viewSettings="viewSettings"
            :selectPerformer="selectPerformer"
            @open-task-drawer="openTaskDrawer"
            @removeTask="updateList"
          />
        </div>
      </div>
    </TirPmScrollbar>
    <div v-if="!tasks.length" class="monitoring-tickets-table__no-find-tasks">
      <TirPmIconView :size="80" :isBordered="false">
        <template #mainSlot>
          <MagnifyingGlassIcon />
        </template>
      </TirPmIconView>
      <div class="monitoring-tickets-table__no-find-tasks-title">Данные не найдены</div>
    </div>
    <TirPmPagination
      v-if="pageResponse?.Current && pagesCount"
      class="monitoring-tickets-table__paginator"
      showMoreElements
      :model-value="pageResponse.Current"
      :range-size="3"
      :pages="pagesCount"
      :options="[10, 20, 30, 40, 50, 100]"
      :count-elements="pageStep"
      @update:count-elements="changePageStep"
      @update:model-value="changeCurrentPage"
    />
    <TaskDrawer ref="$taskDrawer" disable-events />
  </div>
  <div v-else>
    <MonitoringsSkeleton />
  </div>
</template>
<style scoped lang="scss">
.monitoring-tickets-table {
  &__columns-header {
    background-color: var(--bg-base);
    padding-bottom: 0.5rem;
    position: sticky;
    top: 0;
    z-index: 5;
  }
  &__no-find-tasks {
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    gap: 1.5rem;
    height: 100%;
  }
  &__no-find-tasks-title {
    font-size: 1.625rem;
    font-weight: 500;
  }
  &__block {
    height: 100%;
    margin-top: 2.25rem;
  }

  &__scrollbar {
    padding-bottom: 1rem;
    max-height: calc(100vh - 12.5rem);
  }

  &__paginator {
    display: flex;
    justify-content: space-between;
    position: absolute;
    bottom: 0;
    width: 100%;
    margin-top: 0.5rem;
  }

  &__list {
    display: flex;
    flex-direction: column;
    gap: 0.5rem;
    margin-top: 0.5rem;
  }
}
</style>

<style lang="scss">
.monitoring-tickets-table__list
  .tir-pm-context-menu
  .tir-pm-context-menu__target
  .task-card
  .task-card-item-wrapper {
  flex-shrink: 1 !important;
}
.monitoring-tickets-table {
  &__scrollbar {
    height: calc(100% - 2.3rem);
    .hide-scroll {
      height: 100%;
    }
  }
}
</style>
